import React, { useState, useMemo } from "react";
import {
  useGetDestinationsQuery,
  useAddDestinationMutation,
  useToggleDestinationVisitedMutation,
  useDeleteDestinationMutation,
  useUpdateDestinationMutation,
} from "../../features/api/destinationApiSlice";
import { useToast } from "../toast/ToastContext";
import FuelLoading from "../../pages/FuelLoading";
import {
  FaTimes,
  FaSearch,
  FaPrint,
  FaStar,
  FaRegStar,
  FaPen,
  FaPlus,
  FaCheckCircle,
  FaRegCircle,
  FaTrash,
  FaUser,
  FaShareAlt,
} from "react-icons/fa";
import "./adminDestinations.scss";
import ConfirmModal from "../modal/ConfirmModal";
import AdminHeader from "../layout/AdminHeader";

export default function AdminDestinations() {
  const { showToast } = useToast();

  // Workspace Operational Mode Flags
  const [editingId, setEditingId] = useState(null);
  const [adminSearch, setAdminSearch] = useState("");

  // Local form field state inputs
  const [townName, setTownName] = useState("");
  const [stateName, setStateName] = useState("");
  const [routeGroup, setRouteGroup] = useState("");
  const [suggestedBy, setSuggestedBy] = useState("");
  const [platformSource, setPlatformSource] = useState("TikTok");
  const [adminNotes, setAdminNotes] = useState("");

  // Modal Interactive States
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetTitle: "",
  });

  // Data Fetching pipelines & server mutation hooks triggers
  const {
    data: destinations = [],
    isLoading,
    isError,
  } = useGetDestinationsQuery();
  const [addDestination, { isLoading: isCreating }] =
    useAddDestinationMutation();
  const [updateDestination, { isLoading: isUpdating }] =
    useUpdateDestinationMutation();
  const [toggleVisited, { isLoading: isToggling }] =
    useToggleDestinationVisitedMutation();
  const [deleteDest, { isLoading: isDeleting }] =
    useDeleteDestinationMutation();

  const isMutating = isCreating || isUpdating || isToggling || isDeleting;

  const resetFormWorkspace = () => {
    setEditingId(null);
    setTownName("");
    setStateName("");
    setRouteGroup("");
    setSuggestedBy("");
    setPlatformSource("TikTok");
    setAdminNotes("");
  };

  const handleEditInit = (item) => {
    setEditingId(item._id);
    setTownName(item.townName || "");
    setStateName(item.stateName || "");
    setRouteGroup(item.routeGroup || "");
    setSuggestedBy(item.suggestedBy || "");
    setPlatformSource(item.platformSource || "TikTok");
    setAdminNotes(item.adminNotes || "");
  };

  // 📊 COMPUTATION MEMORY MATRIX: Dynamically applies search keywords & groups long town arrays
  const groupedDestinations = useMemo(() => {
    return destinations.reduce((groups, item) => {
      // Inline text search filtering rule check
      if (adminSearch.trim()) {
        const query = adminSearch.toLowerCase();
        const matchesQuery =
          item.townName?.toLowerCase().includes(query) ||
          item.stateName?.toLowerCase().includes(query) ||
          item.routeGroup?.toLowerCase().includes(query) ||
          item.suggestedBy?.toLowerCase().includes(query);

        if (!matchesQuery) return groups;
      }

      const group = item.routeGroup || "Unassigned Routes";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
      return groups;
    }, {});
  }, [destinations, adminSearch]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!townName.trim() || !stateName.trim() || !routeGroup.trim()) {
      showToast("Town, State, and Route Group are strictly required.", "error");
      return;
    }

    const payload = {
      townName: townName.trim(),
      stateName: stateName.trim(),
      routeGroup: routeGroup.trim(),
      suggestedBy: suggestedBy.trim() || undefined,
      platformSource,
      adminNotes: adminNotes.trim(),
    };

    try {
      if (editingId) {
        await updateDestination({ id: editingId, ...payload }).unwrap();
        showToast(`📍 Overwrite saved for ${townName}!`, "success");
      } else {
        await addDestination(payload).unwrap();
        showToast(`📍 ${townName} added to upcoming scouting logs!`, "success");
      }
      resetFormWorkspace();
    } catch (err) {
      console.log("ERROR", err);
      showToast(
        "Failed to write coordinate packets down backend channels.",
        "error"
      );
    }
  };

  const handleCheckoffToggle = async (id) => {
    try {
      await toggleVisited(id).unwrap();
    } catch (err) {
      showToast("Fulfillment state toggling mutation failure.", "error");
    }
  };

  // 🟢 NEW Function: Toggles the dynamic upcoming feature selection
  const handleUpcomingToggle = async (item) => {
    try {
      await updateDestination({
        id: item._id,
        slug: item.slug, // Pass tracking values if mandatory inside your slice
        isUpcoming: !item.isUpcoming,
      }).unwrap();
      showToast(
        item.isUpcoming
          ? `Removed from Upcoming checklist`
          : `Marked as Upcoming Trip Target!`,
        "success"
      );
    } catch (err) {
      showToast("Failed to alter upcoming priority configurations.", "error");
    }
  };

  // 🟢 NEW Function: Generates a clean window print layout window for offline vehicle clip boards
  const handlePrintLedger = () => {
    window.print();
  };

  const handleDeleteTrigger = (id, title) => {
    setModalConfig({ isOpen: true, targetId: id, targetTitle: title });
  };

  const executePurgeItem = async () => {
    const { targetId, targetTitle } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetTitle: "" });
    try {
      await deleteDest(targetId).unwrap();
      showToast(`"${targetTitle}" purged from destination indexes.`, "success");
      if (editingId === targetId) resetFormWorkspace();
    } catch (err) {
      showToast("Failed to wipe entry from database collections.", "error");
    }
  };

  if (isLoading)
    return (
      <div className="admin-dest-fallback">
        <FuelLoading text="Opening town maps ledger..." />
      </div>
    );
  if (isError)
    return (
      <div className="admin-dest-fallback">
        ⚠️ Error loading community scouting data records.
      </div>
    );

  return (
    <div className="admin-destinations-manager fuel-fade-in">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title="Remove Town Suggestion"
        message={`Are you certain you want to delete "${modalConfig.targetTitle}" from your maps registry?`}
        onConfirm={executePurgeItem}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetTitle: "" })
        }
      />

      {isMutating && (
        <FuelLoading text="Updating ledger indexes..." fullScreen />
      )}

      <AdminHeader
        title="Community Suggested Destinations"
        description="Organize towns by geographic routes, track followers recommendations, and mark milestones off the map."
        badgeText="Route Scout Desk"
      />

      {/* 🟢 TOP CONTEXT FILTERS BAR ROW WITH ONE-CLICK PRINTING TRIGGER */}
      <div className="scouting-admin-search-toolbar no-print">
        <div className="admin-search-wrapper">
          <FaSearch className="search-lens-icon" />
          <input
            type="text"
            value={adminSearch}
            onChange={(e) => setAdminSearch(e.target.value)}
            placeholder="Filter list stacks instantly by town name, region route, or user handle..."
          />
          {adminSearch && (
            <button
              type="button"
              className="clear-text-btn"
              onClick={() => setAdminSearch("")}
            >
              ✕
            </button>
          )}
        </div>
        <button
          type="button"
          className="admin-print-btn"
          onClick={handlePrintLedger}
        >
          <FaPrint /> Print List
        </button>
      </div>

      <div className="destinations-columns-grid">
        {/* LEFT PANEL: INTERACTIVE FORM COMPOSITION HUB */}
        <section className="composer-form-card no-print">
          <h2>
            {editingId
              ? "✏️ Edit Destination Parameters"
              : "Log Follower Recommendation"}
          </h2>
          <p>
            {editingId
              ? "Tweak regional assignments, edit notes, or fix text typos."
              : "Record a new location to build future travel itineraries and driving episodes."}
          </p>

          <form onSubmit={handleFormSubmit} className="dest-core-form">
            <div className="field-group">
              <label>Town Name *</label>
              <input
                type="text"
                value={townName}
                onChange={(e) => setTownName(e.target.value)}
                placeholder="e.g., Olin, Monmouth, Galena"
                required
              />
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>State *</label>
                <input
                  type="text"
                  value={stateName}
                  onChange={(e) => setStateName(e.target.value)}
                  placeholder="e.g., Iowa"
                  required
                />
              </div>
              <div className="field-group">
                <label>Route Grouping / Region *</label>
                <input
                  type="text"
                  value={routeGroup}
                  onChange={(e) => setRouteGroup(e.target.value)}
                  placeholder="e.g., Eastern Iowa, Midwest"
                  required
                />
              </div>
            </div>

            <div className="form-input-row halves">
              <div className="field-group">
                <label>Suggested By Username</label>
                <input
                  type="text"
                  value={suggestedBy}
                  onChange={(e) => setSuggestedBy(e.target.value)}
                  placeholder="e.g., @traveler_john"
                />
              </div>
              <div className="field-group">
                <label>Platform Origin Channel</label>
                <select
                  value={platformSource}
                  onChange={(e) => setPlatformSource(e.target.value)}
                >
                  <option value="TikTok">🎵 TikTok</option>
                  <option value="YouTube">🎬 YouTube</option>
                  <option value="Facebook">👥 Facebook</option>
                  <option value="Contact Form">📬 Contact Form</option>
                  <option value="Other">🌐 Other</option>
                </select>
              </div>
            </div>

            <div className="field-group">
              <label>Scouting & Field Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add optional details like 'Famous for historical main street' or 'Great gravel route loops nearby'..."
              />
            </div>

            <div className="form-buttons-row-cluster">
              <button
                type="submit"
                className="fuel-primary-btn log-dest-btn"
                disabled={isMutating}
              >
                {editingId ? (
                  "Save Parameter Changes"
                ) : (
                  <>
                    <FaPlus style={{ marginRight: "8px" }} /> Append Town to Map
                    Ledger
                  </>
                )}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="cancel-editor-btn"
                  onClick={resetFormWorkspace}
                >
                  <FaTimes /> Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ===== RIGHT PANEL: SAVED DIRECTORIES MATRIX SCROLL FEED ===== */}
        <section className="destinations-display-pane">
          {Object.keys(groupedDestinations).length === 0 ? (
            <div className="empty-slate-message">
              No matching destinations found.
            </div>
          ) : (
            Object.entries(groupedDestinations).map(([groupTitle, items]) => (
              <div key={groupTitle} className="route-group-box">
                <h3 className="group-heading-title">
                  🗺️ Route Segment: {groupTitle} ({items.length})
                </h3>

                {/* Isolated scroll wrapper to keep long lists structurally bound under 360px max-height */}
                <div className="admin-scrolling-towns-wrapper">
                  <div className="items-cards-stack">
                    {items.map((item) => (
                      <div
                        key={item._id}
                        className={`dest-row-card ${
                          item.isVisited ? "dest-row-card--visited" : ""
                        } ${
                          editingId === item._id ? "dest-row-card--editing" : ""
                        } ${item.isUpcoming ? "dest-row-card--upcoming" : ""}`}
                      >
                        {/* Checked Status Indicator Toggle Button */}
                        <button
                          type="button"
                          className="checkoff-toggle-action-hitbox no-print"
                          onClick={() => handleCheckoffToggle(item._id)}
                          disabled={isMutating}
                          aria-label="Toggle completed milestone"
                        >
                          {item.isVisited ? (
                            <FaCheckCircle className="icon-check checked" />
                          ) : (
                            <FaRegCircle className="icon-check" />
                          )}
                        </button>

                        <div className="dest-meta-block">
                          <div className="dest-title-line">
                            <h4>
                              {item.townName}, {item.stateName}
                            </h4>
                            {item.isUpcoming && (
                              <span className="upcoming-inline-badge">
                                Upcoming Drive
                              </span>
                            )}
                          </div>
                          <div className="dest-attribution-row">
                            <span>
                              <FaUser /> {item.suggestedBy}
                            </span>
                            <span>
                              <FaShareAlt /> via {item.platformSource}
                            </span>
                          </div>
                          {item.adminNotes && (
                            <p className="dest-notes-text">
                              “{item.adminNotes}”
                            </p>
                          )}
                          {item.isVisited && item.visitDate && (
                            <span className="completion-date-stamp">
                              🏁 Cleared on:{" "}
                              {new Date(item.visitDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {/* Control buttons action bar cluster */}
                        <div className="dest-row-card__controls no-print">
                          <button
                            type="button"
                            className={`btn-row-action star-upcoming-btn ${
                              item.isUpcoming ? "active" : ""
                            }`}
                            onClick={() => handleUpcomingToggle(item)}
                            disabled={isMutating}
                            title="Flag as Upcoming Destination"
                          >
                            {item.isUpcoming ? (
                              <FaStar style={{ color: "#f59e0b" }} />
                            ) : (
                              <FaRegStar />
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn-row-action edit-brush-btn"
                            onClick={() => handleEditInit(item)}
                            disabled={isMutating}
                            title="Edit Parameters"
                          >
                            <FaPen />
                          </button>
                          <button
                            type="button"
                            className="btn-row-action trash-purge-btn"
                            onClick={() =>
                              handleDeleteTrigger(item._id, item.townName)
                            }
                            disabled={isMutating}
                            title="Purge Record"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}
